/**
 * BookingFlows Connect — The Network (LinkedIn-style marketplace browse).
 * Search + category chips + visual service cards. Pick anything, set YOUR
 * markup, drop it into one of your flows.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../../lib/apiError';
import { toast } from 'sonner';
import { connectApi } from '../../utils/apiV2';
import { ConnectShell, useConnectMe, money, CARD } from '../../components/connect/ConnectShell';
import { Search, MapPin, Clock, Plus, Check, Loader2, X, Layers, Globe } from 'lucide-react';

const AddToFlowModal = ({ service, onClose, onAdded }) => {
  const [flows, setFlows] = useState(null);
  const [selected, setSelected] = useState('');
  const [newName, setNewName] = useState('');
  const [markup, setMarkup] = useState('15');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    connectApi.flows().then((r) => {
      const f = r.data.flows || [];
      setFlows(f);
      if (f.length > 0) setSelected(f[0].id);
      else setSelected('__new__');
    }).catch(() => setFlows([]));
  }, []);

  const pct = parseFloat(markup);
  const validPct = !Number.isNaN(pct) && pct >= 0 && pct <= 100;
  const yourPrice = validPct ? service.price * (1 + pct / 100) : null;

  const submit = async () => {
    if (!validPct) return toast.error('Markup must be between 0 and 100%');
    let flowId = selected;
    setBusy(true);
    try {
      if (selected === '__new__') {
        if (!newName.trim()) { setBusy(false); return toast.error('Give your new flow a name'); }
        const r = await connectApi.createFlow({ name: newName.trim() });
        flowId = r.data.id;
      }
      await connectApi.addFlowItem(flowId, { serviceId: service.id, markupPct: pct });
      toast.success(`"${service.name}" added at +${pct}%`);
      onAdded();
      onClose();
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not add service'));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="connect-add-modal">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 cx-rise">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-600">Add to a flow</p>
            <h3 className="text-lg font-extrabold text-slate-900 truncate">{service.name}</h3>
            <p className="text-xs text-slate-400">{service.operator.name} · base {money(service.price, service.currency)}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 shrink-0" data-testid="connect-add-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
          {!flows && <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>}
          {(flows || []).map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setSelected(f.id)}
              data-testid={`connect-add-flow-option-${f.id}`}
              className={`w-full flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors ${selected === f.id ? 'border-emerald-500 bg-emerald-50/60' : 'border-slate-200 hover:border-slate-300'}`}
            >
              <Layers className={`w-4 h-4 ${selected === f.id ? 'text-emerald-600' : 'text-slate-400'}`} />
              <span className="flex-1 text-sm font-bold text-slate-800 truncate">{f.name}</span>
              <span className="text-[11px] text-slate-400">{f.items.length} service{f.items.length !== 1 ? 's' : ''}</span>
              {selected === f.id && <Check className="w-4 h-4 text-emerald-600" />}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSelected('__new__')}
            data-testid="connect-add-flow-new"
            className={`w-full flex items-center gap-3 rounded-xl border border-dashed px-3.5 py-2.5 text-left transition-colors ${selected === '__new__' ? 'border-emerald-500 bg-emerald-50/60' : 'border-slate-300 hover:border-slate-400'}`}
          >
            <Plus className={`w-4 h-4 ${selected === '__new__' ? 'text-emerald-600' : 'text-slate-400'}`} />
            <span className="text-sm font-bold text-slate-700">Create a new flow</span>
          </button>
          {selected === '__new__' && (
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Flow name — e.g. Dubai Weekend Picks"
              data-testid="connect-add-flow-name-input"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          )}
        </div>

        <div className="mt-4 rounded-2xl bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-slate-700">Your markup</p>
              <p className="text-[11px] text-slate-400">On top of the operator's price (0–100%)</p>
            </div>
            <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3.5 py-2">
              <span className="text-xs text-slate-400 font-bold">+</span>
              <input
                type="number" min="0" max="100" step="0.5" value={markup}
                onChange={(e) => setMarkup(e.target.value)}
                data-testid="connect-add-markup-input"
                className="w-14 text-sm font-extrabold text-slate-900 focus:outline-none"
              />
              <span className="text-xs text-slate-400 font-bold">%</span>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-slate-500 font-semibold">Customer pays</span>
            <span className="font-extrabold text-emerald-700" data-testid="connect-add-your-price">
              {yourPrice != null ? money(yourPrice, service.currency) : '—'}
            </span>
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-400 mt-0.5">
            <span>Your cut per booking</span>
            <span className="font-bold">{yourPrice != null ? money(yourPrice - service.price, service.currency) : '—'}</span>
          </div>
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={submit}
          data-testid="connect-add-confirm-btn"
          className="mt-4 w-full rounded-full bg-emerald-600 text-white text-sm font-extrabold py-3 hover:bg-emerald-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add to flow
        </button>
      </div>
    </div>
  );
};

const ServiceCard = ({ svc, onAdd, delay }) => (
  <div className={`${CARD} overflow-hidden flex flex-col cx-rise`} style={{ animationDelay: `${delay}ms` }} data-testid={`connect-card-${svc.id}`}>
    <div className="relative h-40 bg-gradient-to-br from-emerald-100 to-teal-50">
      {svc.image && <img src={svc.image} alt={svc.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute top-2.5 left-2.5 flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[10px] font-extrabold text-slate-700 shadow-sm">
        <MapPin className="w-3 h-3 text-emerald-600" />
        {[svc.city, svc.country].filter(Boolean).join(', ') || 'Location TBD'}
      </div>
      {svc.category && (
        <div className="absolute bottom-2.5 left-2.5 rounded-full bg-slate-900/70 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-white capitalize">
          {svc.category.replace(/-/g, ' ')}
        </div>
      )}
    </div>
    <div className="p-4 flex flex-col flex-1">
      <p className="text-sm font-extrabold text-slate-900 leading-snug line-clamp-2">{svc.name}</p>
      <p className="text-[11px] text-slate-400 mt-1 truncate">{svc.operator.name}</p>
      <div className="mt-2.5 flex items-center gap-3 text-[11px] text-slate-500 font-semibold">
        <span className="text-base font-extrabold text-slate-900">{money(svc.price, svc.currency)}</span>
        {svc.durationMinutes ? (
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{svc.durationMinutes >= 60 ? `${Math.round(svc.durationMinutes / 60)}h` : `${svc.durationMinutes}m`}</span>
        ) : null}
      </div>
      <div className="mt-3 pt-3 border-t border-slate-50">
        {svc.inFlows.length > 0 ? (
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-extrabold px-2.5 py-1.5 truncate">
              <Check className="w-3.5 h-3.5 shrink-0" /> In {svc.inFlows.length} flow{svc.inFlows.length !== 1 ? 's' : ''} · +{svc.inFlows[0].markupPct}%
            </span>
            <button type="button" onClick={() => onAdd(svc)} data-testid={`connect-card-add-again-${svc.id}`} className="text-[11px] font-bold text-slate-400 hover:text-emerald-600 shrink-0">+ Add</button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onAdd(svc)}
            data-testid={`connect-card-add-${svc.id}`}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-extrabold py-2.5 hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add to flow
          </button>
        )}
      </div>
    </div>
  </div>
);

export default function ConnectMarketplace() {
  const { me, refresh } = useConnectMe();
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [modalSvc, setModalSvc] = useState(null);

  const load = useCallback((params = {}) => {
    connectApi.catalog(params).then((r) => setCatalog(r.data)).catch(() => setCatalog({ services: [], categories: [], cities: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSearch = (over = {}) => {
    const p = { q: search || undefined, category: category || undefined, city: city || undefined, ...over };
    Object.keys(p).forEach((k) => p[k] === undefined && delete p[k]);
    load(p);
  };

  return (
    <ConnectShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10" data-testid="connect-marketplace">
        <div className="mb-6 cx-rise">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-2 flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" /> Every experience on BookingFlows
          </p>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">The Network</h1>
          <p className="text-sm text-slate-500 mt-1.5 max-w-2xl leading-relaxed">
            Resell anything here with your own markup — your cut pays straight to your personal Stripe account.
            {me?.affiliate && !me.affiliate.payoutReady && <span className="font-bold text-emerald-700"> Set up payouts in Earnings to get paid.</span>}
          </p>
        </div>

        {/* Search bar */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4 cx-rise" style={{ animationDelay: '80ms' }}>
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              placeholder="Search experiences, boats, tours…"
              data-testid="connect-search-input"
              className="w-full rounded-full border border-slate-200 bg-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/25 shadow-sm"
            />
          </div>
          <select
            value={city}
            onChange={(e) => { setCity(e.target.value); runSearch({ city: e.target.value || undefined }); }}
            data-testid="connect-city-select"
            className="rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 focus:outline-none shadow-sm"
          >
            <option value="">All locations</option>
            {(catalog?.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="button" onClick={() => runSearch()} data-testid="connect-search-btn" className="rounded-full bg-slate-900 text-white text-sm font-extrabold px-6 py-3 hover:bg-emerald-700 transition-colors shadow-sm">
            Search
          </button>
        </div>

        {/* Category chips */}
        {(catalog?.categories || []).length > 0 && (
          <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1 cx-rise" style={{ animationDelay: '140ms' }}>
            <button type="button" onClick={() => { setCategory(''); runSearch({ category: undefined }); }} data-testid="connect-category-all" className={`rounded-full px-3.5 py-1.5 text-xs font-bold whitespace-nowrap transition-colors ${!category ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-300'}`}>All</button>
            {catalog.categories.map((c) => (
              <button key={c} type="button" onClick={() => { setCategory(c); runSearch({ category: c }); }} data-testid={`connect-category-${c}`} className={`rounded-full px-3.5 py-1.5 text-xs font-bold whitespace-nowrap capitalize transition-colors ${category === c ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-300'}`}>{c.replace(/-/g, ' ')}</button>
            ))}
          </div>
        )}

        {/* Cards */}
        {!catalog && <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-300" /></div>}
        {catalog && catalog.services.length === 0 && (
          <div className={`${CARD} border-dashed p-12 text-center`}>
            <Globe className="w-9 h-9 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-600">No experiences match</p>
            <p className="text-xs text-slate-400 mt-1">Try a different search — new operators join the network automatically.</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="connect-catalog-grid">
          {(catalog?.services || []).map((svc, i) => (
            <ServiceCard key={svc.id} svc={svc} delay={Math.min(i * 40, 400)} onAdd={setModalSvc} />
          ))}
        </div>
      </div>

      {modalSvc && (
        <AddToFlowModal
          service={modalSvc}
          onClose={() => setModalSvc(null)}
          onAdded={() => { runSearch(); refresh(); }}
        />
      )}
    </ConnectShell>
  );
}
